import React, { FC } from "react";
import { formatPhone, unformatPhone } from "~/helpers/phone";
import InfoCircle from "../Icons/outline/InfoCircle";
import Input from "../Input";
import Label from "../Label";
import Tooltip from "../Tooltip";
import ContactFormStyled from "./ContactFormStyled";
import { REGEX_PATTERNS } from "~/constants/contact-credit";
import { NoArgVoid } from "~/types/common";
import { ContactCreditRegister, ContactErrors } from "~/types/contact-credit";
import { trackEvent } from "~/helpers/tracking";
import {
  formatSmartQuotes,
  formatNameSpacing,
  validateFirstName,
  validateLastName,
  validatePhoneNumber,
} from "~/helpers/contact";

interface Props {
  errors: ContactErrors;
  register: ContactCreditRegister;
  handleSubmit: NoArgVoid;
  setValue: (name: string, value: string) => void;
  contact?: {
    verifyPhone?: {
      enabled: boolean;
    };
    labels?: {
      firstName?: string;
      lastName?: string;
      emailAddress?: string;
      phoneNumber?: string;
    };
  };
}

const ContactForm: FC<Props> = ({ errors, register, handleSubmit, setValue, contact }) => {
  const handleTCPAMarketingEvent = ({ target }) => {
    trackEvent({
      action: "elementClicked",
      data: {
        text: `CONSENT TO RECEIVE COMMUNICATIONS${target?.checked ? "" : " REVOKED"}`,
        elementType: "CHECKBOX",
        position: "EMAIL REMARKETING",
        location: "order_summary_page",
      },
    });
  };

  const handleFieldInputted = (event) => {
    const input = event.target;
    // check if the input field background is lightblue
    const style = window?.getComputedStyle(input);
    const isAutofilled = style?.backgroundColor === "rgb(232, 240, 254)";

    if (event.target.type != "checkbox") {
      setTimeout(() => {
        trackEvent({
          action: "fieldInputted",
          data: {
            autofilled: isAutofilled,
            fieldLabel: event.target.name,
            fieldId: event.target.id,
            fieldType: "text",
            location: "contact_creation_page",
          },
          event,
        });
      }, 500);
    }
  };

  return (
    <ContactFormStyled
      onBlur={handleFieldInputted}
      onSubmit={handleSubmit}
      phoneVerifyEnabled={contact?.verifyPhone?.enabled === true}
    >
      <div className="contact-form__row">
        <div className="contact-form__group">
          <Label htmlFor="first-name">{contact?.labels?.firstName || "First name"}</Label>
          <Input
            data-testid="first-name"
            id="first-name"
            autoComplete="given-name"
            masked
            type="text"
            registerProps={register("firstname", {
              validate: validateFirstName,
              onBlur: (e) => {
                e.target.value = formatSmartQuotes(e.target.value);
                e.target.value = formatNameSpacing(e.target.value, true);
                setValue("firstname", e.target.value);
              },
            })}
            error={errors?.firstname?.message}
          />
        </div>
        <div className="contact-form__group contact-form__group--last-name">
          <Label htmlFor="last-name">{contact?.labels?.lastName || "Last name"}</Label>
          <Input
            data-testid="last-name"
            id="last-name"
            autoComplete="family-name"
            masked
            type="text"
            registerProps={register("lastname", {
              validate: validateLastName,
              onBlur: (e) => {
                e.target.value = formatSmartQuotes(e.target.value);
                e.target.value = formatNameSpacing(e.target.value);
                setValue("lastname", e.target.value);
              },
            })}
            error={errors?.lastname?.message}
          />
        </div>
      </div>
      <div className="contact-form__row">
        <div className="contact-form__group">
          <Label htmlFor="email-address">
            {contact?.labels?.emailAddress || "Email Address"}
            <Tooltip
              tabIndex={0}
              theme="light"
              html={
                <div style={{ width: 300 }}>
                  We'll email your order confirmation and plan updates to you. Your email address is secured, and we'll
                  never sell it.
                </div>
              }
            >
              <InfoCircle />
            </Tooltip>
          </Label>
          <Input
            data-testid="email-address"
            id="email-address"
            autoComplete="email"
            masked
            type="email"
            registerProps={register("email", {
              required: "Email is required.",
              pattern: {
                value: REGEX_PATTERNS.EMAIL,
                message: "Invalid email address format",
              },
            })}
            error={errors?.email?.message}
          />
        </div>
      </div>
      <div className="contact-form__row">
        <div className="contact-form__group">
          <Label
            htmlFor="phone-number"
            subLabel={contact?.verifyPhone?.enabled === true ? "" : "mobile number preferred"}
          >
            {contact?.labels?.phoneNumber || "Phone Number"}
          </Label>
          <Input
            data-testid="phone-number"
            id="phone-number"
            autoComplete="tel"
            masked
            type="tel"
            registerProps={register("phone", {
              required: "Phone number is required.",
              validate: validatePhoneNumber,
              maxLength: 12,
            })}
            // Use the onInput event instead of onChange
            onInput={(e) => {
              e.currentTarget.value = formatPhone(unformatPhone(e.currentTarget.value));
            }}
            error={errors?.phone?.message}
          />
        </div>
      </div>
      {contact?.verifyPhone?.enabled !== true && (
        <div className="contact-form__row">
          <div className="contact-form__group">
            <Label className="contact-form__tcpa_marketing">
              <Input
                onClick={handleTCPAMarketingEvent}
                type="checkbox"
                registerProps={register("tcpaMarketing")}
                data-chsn-reward-click="cart contact consent"
              />
              <span>
                By checking this box, you consent to receive communications from Frontier about our products and
                services at the number provided, including calls, texts and emails. These communications may be
                generated using automated technology. You understand that providing your consent is not required to make
                a purchase.
              </span>
            </Label>
          </div>
        </div>
      )}
    </ContactFormStyled>
  );
};

ContactForm.displayName = "ContactForm";

export default ContactForm;
